import { store } from './store';
import { saveGame } from './save';
import { migrateSave } from '../sim/migrate';

/**
 * Zero-knowledge cloud save. The save is AES-GCM encrypted on THIS device before
 * upload; the Worker (workers/save-worker.js) stores only ciphertext keyed by a
 * random per-device id. The id + key never derive from personal data and never
 * leave the device except in the one-time "farm code" the owner can carry to a
 * new phone. Offline-first: localStorage is the source of truth; we push when we
 * can and pull a newer copy on boot.
 */
const CLOUD_URL = 'https://sandy-hollow-save.hocmemini.workers.dev';
const ID_KEY = 'sandyhollow.farmid';
const KEY_KEY = 'sandyhollow.farmkey';
const LOCAL_AT = 'sandyhollow.localAt';

export const cloudEnabled = (): boolean =>
  CLOUD_URL.length > 0 && typeof crypto !== 'undefined' && !!crypto.subtle;

function b64url(b: Uint8Array): string {
  let s = '';
  for (const x of b) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function unb64url(s: string): Uint8Array<ArrayBuffer> {
  const t = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(t);
  const b = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) b[i] = bin.charCodeAt(i);
  return b;
}
function ls(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function setLs(k: string, v: string): void {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* ignore */
  }
}

function farmId(): string {
  let id = ls(ID_KEY) ?? '';
  if (!id) {
    id = b64url(crypto.getRandomValues(new Uint8Array(16)));
    setLs(ID_KEY, id);
  }
  return id;
}

let cachedKey: CryptoKey | null = null;
async function getKey(): Promise<CryptoKey> {
  if (cachedKey) return cachedKey;
  const stored = ls(KEY_KEY);
  if (stored) {
    cachedKey = await crypto.subtle.importKey('raw', unb64url(stored), { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
    return cachedKey;
  }
  const k = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  setLs(KEY_KEY, b64url(new Uint8Array(await crypto.subtle.exportKey('raw', k))));
  cachedKey = k;
  return k;
}

/** The portable farm code = id(16) + key(32), base64url. Save it to restore on a new phone. */
export async function getFarmCode(): Promise<string> {
  const id = unb64url(farmId());
  await getKey();
  const key = unb64url(ls(KEY_KEY)!);
  const out = new Uint8Array(id.length + key.length);
  out.set(id);
  out.set(key, id.length);
  return b64url(out);
}
/** Load a farm code from another device. Returns false if malformed. */
export function setFarmCode(code: string): boolean {
  try {
    const b = unb64url(code.trim());
    if (b.length !== 48) return false;
    setLs(ID_KEY, b64url(b.slice(0, 16)));
    setLs(KEY_KEY, b64url(b.slice(16)));
    setLs(LOCAL_AT, '0'); // force the next pull to adopt the cloud copy
    cachedKey = null;
    return true;
  } catch {
    return false;
  }
}

async function fetchT(url: string, opts: RequestInit, ms: number): Promise<Response> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...opts, signal: ctrl.signal });
  } finally {
    clearTimeout(t);
  }
}

let dirty = false;
let busy = false;
export function markDirty(): void {
  dirty = true;
}

export async function flush(): Promise<void> {
  if (!cloudEnabled() || !dirty || busy) return;
  busy = true;
  try {
    const key = await getKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const plain = new TextEncoder().encode(JSON.stringify(store.get()));
    const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain));
    const at = Number(ls(LOCAL_AT) ?? Date.now());
    const r = await fetchT(
      `${CLOUD_URL}/${farmId()}`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ savedAt: at, iv: b64url(iv), ct: b64url(ct) }) },
      8000,
    );
    if (r.ok) dirty = false;
  } catch {
    /* stay dirty; retry when back online */
  } finally {
    busy = false;
  }
}

/** On boot: adopt the cloud save if it's newer than this device's last save. */
export async function pullIfNewer(): Promise<boolean> {
  if (!cloudEnabled()) return false;
  try {
    const r = await fetchT(`${CLOUD_URL}/${farmId()}`, {}, 3000);
    if (!r.ok) return false;
    const p = (await r.json()) as { savedAt: number; iv: string; ct: string };
    if (!p.iv || !p.ct || p.savedAt <= Number(ls(LOCAL_AT) ?? 0)) return false;
    const key = await getKey();
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64url(p.iv) }, key, unb64url(p.ct));
    const m = migrateSave(JSON.parse(new TextDecoder().decode(plain)));
    if (!m.ok) return false;
    store.replace(m.state);
    saveGame(m.state);
    setLs(LOCAL_AT, String(p.savedAt));
    return true;
  } catch {
    return false; // offline, or a code/key that doesn't match this farm
  }
}

export function initCloud(): void {
  if (!cloudEnabled()) return;
  store.subscribe(() => markDirty());
  window.addEventListener('online', () => void flush());
  setInterval(() => void flush(), 30000);
}
